import md5 from "md5";
import * as React from "react";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { fetchAircraftWithCache, fetchHangar } from "../../../hooks/api";
import { extractImportantLocationState } from "../../../hooks/useHangar";
import { useStateHistory } from "../../../hooks/useMeaningfulStateHistory";
import { usePredefinedObstacle } from "../../../hooks/usePredefinedObstacle";
import { useScheduleUrl } from "../../../hooks/useScheduleUrl";
import { PlacementService } from "../../../services/PlacementService";
import { useScheduleTimeRange } from "../../../stores/useScheduleTimeRange";
import { Aircraft, Hangar, Stack as StackType, Tenant } from "../../../types";
import { defaultPreferenceForAircraft } from "../../../utils";
import { detectAndLogDuplicates } from "../../../utils/schedulingHelpers";
import {
  ceilToQuarterHour,
  floorToQuarterHour,
} from "../../../utils/timeUtils";
import { HangarStack } from "../../Hangar/Hangar";
import { useLayout } from "../../Ramp/CollapsibleRampActionCard";
import { ScheduledHangarViewPresenter } from "./ScheduledHangarViewPresenter";

const { useTransition } = React;

interface ScheduledHangarViewProps {
  id: string;
  stacks: HangarStack[];
  setStacks: (stacks: HangarStack[]) => void;
  isReference?: boolean;
  isAlgorithmTesting?: boolean;
}

interface ScheduledViewState {
  scheduledHangar: Hangar | null;
  timestamp: Date;
}

// Predicate function to determine if a scheduled state change is meaningful for undo
const isScheduledStateChangeMeaningful = (
  oldState: ScheduledViewState,
  newState: ScheduledViewState
): boolean => {
  // If either state is null, it's meaningful
  if (!oldState?.scheduledHangar || !newState?.scheduledHangar) {
    return true;
  }

  // If timestamp changed, it's meaningful
  if (oldState.timestamp.getTime() !== newState.timestamp.getTime()) {
    return true;
  }

  // Check if hangar positions changed using existing logic
  const oldStateHash = md5(
    JSON.stringify(extractImportantLocationState(oldState.scheduledHangar))
  );
  const newStateHash = md5(
    JSON.stringify(extractImportantLocationState(newState.scheduledHangar))
  );

  return oldStateHash !== newStateHash;
};

export const ScheduledHangarView: React.FC<ScheduledHangarViewProps> =
  React.memo(({ id, stacks, setStacks, isReference, isAlgorithmTesting }) => {
    // URL-driven timestamp management with current time as default
    const {
      currentTime: currentTimestamp,
      setCurrentTime: setCurrentTimestamp,
    } = useScheduleUrl({
      defaultDate: new Date(),
      updateUrlOnMount: true,
    });
    const { postgrest } = useApi();
    const { activeFBO } = useActiveFBOs();
    const { predefinedObstacles } = usePredefinedObstacle();
    const { isDrawerOpen, setIsDrawerOpen } = useLayout();
    const { rangeStart, rangeEnd, setTimeRange, getDefaultWeekRange } =
      useScheduleTimeRange();

    // Memoize PlacementService instance to avoid recreating on every render
    const placementService = React.useMemo(
      () => new PlacementService(postgrest),
      [postgrest]
    );

    // State for the base hangar structure and scheduled data
    const [baseHangar, setBaseHangar] = React.useState<Hangar | null>(null);

    // Composite state for undo functionality (combines hangar state + timestamp)
    const [viewState, setViewState, undoViewState] =
      useStateHistory<ScheduledViewState>(
        { scheduledHangar: null, timestamp: currentTimestamp },
        isScheduledStateChangeMeaningful
      );

    // Extract current values for easier access
    const scheduledHangar = viewState?.scheduledHangar;
    const setScheduledHangar = React.useCallback(
      (newHangar: Hangar | null) => {
        setViewState({
          scheduledHangar: newHangar,
          timestamp: currentTimestamp,
        });
      },
      [setViewState, currentTimestamp]
    );
    // Preserve original stack ID before it gets corrupted by synthetic scheduled stack
    const [originalStackId, setOriginalStackId] = React.useState<string | null>(
      null
    );

    // React 18 concurrent features for smooth date transitions
    const [isPending, startTransition] = useTransition();
    const [initialLoading, setInitialLoading] = React.useState(true);
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);

    // State to trigger refresh
    const [refreshKey, setRefreshKey] = React.useState(0);

    // Track tenant ID -> trip ID mapping
    const [tenantTripMap, setTenantTripMap] = React.useState<
      Map<string, string>
    >(new Map());

    // State for placement data (needed for trip duration display)
    const [allPlacements, setAllPlacements] = React.useState<any[]>([]);

    // State for ALL placements in time range across all locations (for Timeline Aircraft)
    const [allPlacementsInTimeRange, setAllPlacementsInTimeRange] =
      React.useState<any[]>([]);

    // State for trip counts by date (used for calendar date picker indicators)
    // Efficiently loaded from database aggregation instead of client-side computation
    const [tripCountsByDate, setTripCountsByDate] = React.useState<
      Map<string, number>
    >(new Map());

    // State for next/previous trip availability
    const [hasNextTrip, setHasNextTrip] = React.useState(false);
    const [hasPreviousTrip, setHasPreviousTrip] = React.useState(false);
    // Aircraft-specific trip availability
    const [hasNextTripForAircraft, setHasNextTripForAircraft] =
      React.useState(false);
    const [hasPreviousTripForAircraft, setHasPreviousTripForAircraft] =
      React.useState(false);

    // Use global snackbar
    const snackbar = useSnackbar();

    // State for Add Obstacle Dialog
    const [showAddObstacleDialog, setShowAddObstacleDialog] =
      React.useState(false);

    // State for user-added obstacles (separate from baseHangar to avoid circular dependency)
    const [addedObstacles, setAddedObstacles] = React.useState<any[]>([]);

    // Add logging wrapper for setAddedObstacles
    const setAddedObstaclesWithLogging = React.useCallback(
      (newObstaclesOrUpdater: any[] | ((prev: any[]) => any[])) => {
        if (typeof newObstaclesOrUpdater === "function") {
          setAddedObstacles((prev) => {
            const newValue = newObstaclesOrUpdater(prev);
            return newValue;
          });
        } else {
          setAddedObstacles(newObstaclesOrUpdater);
        }
      },
      []
    ); // Empty dependencies to prevent infinite loops

    // Database functions handle placement changes atomically - no separate handler needed
    // Load base hangar structure (dimensions, obstacles, etc.)
    React.useEffect(() => {
      if (!postgrest || !activeFBO || !id) return;

      const loadBaseHangar = async () => {
        try {
          const hangar = await fetchHangar(
            postgrest,
            activeFBO,
            id,
            isReference || false
          );
          setBaseHangar(hangar);
          // Capture the original real stack ID before it gets corrupted
          setOriginalStackId(hangar.stack.id);
        } catch (error) {
          console.error("Failed to load base hangar:", error);
          snackbar.notify({
            text: "Failed to load hangar structure",
            severity: "error",
          });
        }
      };

      loadBaseHangar();
    }, [postgrest, activeFBO, id, isReference]);

    // Load all placements in the visible time range (for Active Trips metric)
    React.useEffect(() => {
      if (!postgrest || !id || !rangeStart || !rangeEnd) {
        return;
      }

      const fetchPlacementsInRange = async () => {
        try {
          // Fetch ALL trips (including unassigned) for search capability (1 year range)
          // Uses the comprehensive view that includes trips without placement assignments
          // HangarLeftPanel will filter this down to rangeStart/rangeEnd for timeline view
          const searchEndDate = new Date();
          searchEndDate.setFullYear(searchEndDate.getFullYear() + 1); // 1 year ahead
          const allPlacements =
            await placementService.getAllTripsWithPlacementStatus(
              rangeStart, // from timeline range start (includes currently active trips)
              searchEndDate // to 1 year from now
            );

          setAllPlacementsInTimeRange(allPlacements);
        } catch (error) {
          console.error("Failed to fetch placements in time range:", error);
        }
      };

      fetchPlacementsInRange();
    }, [postgrest, id, placementService, rangeStart, rangeEnd, refreshKey]);

    // Load trip counts by date for calendar indicators (1 year range)
    React.useEffect(() => {
      if (!postgrest || !id) return;

      const fetchTripCounts = async () => {
        try {
          const startDate = rangeStart || new Date();
          const endDate = new Date(startDate);
          endDate.setFullYear(endDate.getFullYear() + 1); // 1 year ahead for calendar

          const counts = await placementService.getTripCountsByDate(
            startDate,
            endDate,
            id,
            "hangar"
          );

          setTripCountsByDate(counts);
        } catch (error) {
          // Silent failure - calendar indicators are non-critical
          console.warn("Failed to load trip counts for calendar:", error);
        }
      };

      fetchTripCounts();
    }, [postgrest, id, placementService, rangeStart, refreshKey]);

    // Compute selected trip info BEFORE using it in useEffect below
    const selectedTripInfo = React.useMemo(() => {
      if (!scheduledHangar?.stack?.tenants) {
        return null;
      }

      const selectedTenant = scheduledHangar.stack.tenants.find(
        (tenant) => tenant.selected
      );

      if (!selectedTenant) {
        return null;
      }

      const tripId = tenantTripMap.get(selectedTenant.id);
      if (!tripId) {
        return null;
      }

      // Find the trip placement to get the trip data
      // Note: allPlacements only has one placement per trip (deduplicated)
      // TODO: To support multi-segment trips in HangarView, need to use a different data source
      const tripPlacement = allPlacements.find(
        (placement) => placement.trip_id === tripId
      );

      if (!tripPlacement) {
        return null;
      }

      // Use the full trip dates from the placement data - try different property names
      const startDate = tripPlacement.arrival_date || tripPlacement.start_time;
      const endDate = tripPlacement.departure_date || tripPlacement.end_time;

      return {
        start: new Date(startDate),
        end: new Date(endDate),
        tailNumber: selectedTenant.tail_number,
      };
    }, [scheduledHangar?.stack?.tenants, tenantTripMap, allPlacements]);

    // Check for next/previous trips availability
    React.useEffect(() => {
      if (!placementService || !id || !currentTimestamp) return;

      const checkTripAvailability = async () => {
        try {
          // Always check location-wide trips
          const [nextTrip, previousTrip] = await Promise.all([
            placementService.getNextTripTime(id, "hangar", currentTimestamp),
            placementService.getPreviousTripTime(
              id,
              "hangar",
              currentTimestamp
            ),
          ]);

          setHasNextTrip(!!nextTrip);
          setHasPreviousTrip(!!previousTrip);

          // If aircraft selected, also check aircraft-specific trips
          if (selectedTripInfo?.tailNumber) {
            const [nextAircraftTrip, previousAircraftTrip] = await Promise.all([
              placementService.getNextTripTimeForAircraft(
                selectedTripInfo.tailNumber,
                currentTimestamp,
                id,
                "hangar"
              ),
              placementService.getPreviousTripTimeForAircraft(
                selectedTripInfo.tailNumber,
                currentTimestamp,
                id,
                "hangar"
              ),
            ]);
            setHasNextTripForAircraft(!!nextAircraftTrip);
            setHasPreviousTripForAircraft(!!previousAircraftTrip);
          } else {
            // No aircraft selected, clear aircraft-specific state
            setHasNextTripForAircraft(false);
            setHasPreviousTripForAircraft(false);
          }
        } catch (error) {
          console.error("Failed to check trip availability:", error);
        }
      };

      checkTripAvailability();
    }, [placementService, id, currentTimestamp, selectedTripInfo, refreshKey]);

    // Load scheduled placement data when timestamp changes
    React.useEffect(() => {
      if (!postgrest || !activeFBO || !baseHangar || !currentTimestamp) {
        // Don't set loading false here - let the data fetch complete first
        return;
      }

      const loadScheduledData = async () => {
        // Only show loading on initial load, keep previous data visible during date changes
        if (!scheduledHangar) {
          setInitialLoading(true);
        }
        // Don't set isUpdating during date changes - keep previous content visible
        try {
          // 1. Fetch placements at the current timestamp
          const allPlacements = await placementService.getPlacementsAtTime(
            currentTimestamp
          );

          // 2. Filter placements for this hangar
          const hangarPlacements = allPlacements.filter(
            (p) => p.hangar_id === id
          );

          // 2.1 Deduplicate placements by trip_id (keep most recent)
          const placementMap = new Map();
          hangarPlacements.forEach((placement) => {
            const existing = placementMap.get(placement.trip_id);
            if (
              !existing ||
              new Date(placement.created_at) > new Date(existing.created_at)
            ) {
              placementMap.set(placement.trip_id, placement);
            }
          });

          const deduplicatedPlacements = Array.from(placementMap.values());

          // Log if duplicates were found
          if (deduplicatedPlacements.length !== hangarPlacements.length) {
          }

          // Store placement data in state for trip duration display (use deduplicated)
          setAllPlacements(deduplicatedPlacements);

          // 3. Extract unique aircraft IDs and fetch aircraft data
          const aircraftIds = deduplicatedPlacements
            .map((p) => p.aircraft_id)
            .filter((id) => id != null) as string[];

          const aircrafts =
            aircraftIds.length > 0
              ? await fetchAircraftWithCache(postgrest, aircraftIds, activeFBO)
              : [];

          // 4. Create aircraft lookup map
          const aircraftMap = new Map<string, Aircraft>();
          aircrafts.forEach((aircraft) => {
            aircraftMap.set(aircraft.id, aircraft);
          });

          // 5. Create scheduled stack ID - use stable ID to prevent remounting
          const scheduledStackId = baseHangar.stack.id;

          // Build trip ID map
          const tripMap = new Map<string, string>();

          // Find currently selected tenant's tail number to preserve selection across time changes
          const currentlySelectedTailNumber =
            scheduledHangar?.stack?.tenants?.find(
              (t) => t.selected
            )?.tail_number;

          // Transform placements to tenants format
          const scheduledTenants: Tenant[] = deduplicatedPlacements.map(
            (placement) => {
              const aircraft = placement.aircraft_id
                ? aircraftMap.get(placement.aircraft_id)
                : undefined;

              // Fail fast if aircraft data is missing
              if (!aircraft) {
                throw new Error(
                  `Missing aircraft data for placement ${placement.id} (aircraft_id: ${placement.aircraft_id}, tail_number: ${placement.tail_number}). ` +
                    `This indicates a data integrity issue - aircraft records should exist for all scheduled placements.`
                );
              }

              const tenantId = `scheduled-trip-${placement.trip_id}`;

              // Store the mapping
              tripMap.set(tenantId, placement.trip_id);

              return {
                id: tenantId,
                fbo_id: baseHangar.fbo_id,
                entity_id: placement.entity_id || `entity-${placement.trip_id}`,
                placement_id: placement.id,
                aircraft_id: placement.aircraft_id || "",
                type: placement.aircraft_type || "transient",
                tail_number: placement.tail_number || "Unknown",
                owner_manager: placement.owner_manager || "",
                trip_id: placement.trip_id,
                aircraft,
                created_at: placement.start_time,
                // Preserve selection state for the same aircraft across time changes
                selected: currentlySelectedTailNumber === placement.tail_number,
                position: {
                  id: `position-${placement.id}`,
                  stack_id: scheduledStackId, // Use the scheduled stack ID, not the base hangar stack ID
                  entity_id:
                    placement.entity_id || `entity-${placement.trip_id}`,
                  x: placement.x,
                  y: placement.y,
                  angle: placement.angle,
                  preferences: aircraft
                    ? defaultPreferenceForAircraft(aircraft, activeFBO)
                    : [],
                  timestamp: placement.start_time
                    ? new Date(placement.start_time).getTime()
                    : Date.now(), // Use placement start time (changes when position changes)
                },
                suggestedPositions: [],
                note: `Trip: ${placement.trip_id}`,
                note_last_updated_by: "scheduling",
                note_last_updated: new Date().toISOString(),
              };
            }
          );

          // Log any remaining duplicates for monitoring
          detectAndLogDuplicates(scheduledTenants, `ScheduledHangarView-${id}`);

          // 6. Create scheduled stack

          // Merge base obstacles (from database) with user-added obstacles

          const allObstacles = [
            ...(baseHangar.stack.movableObstacles || []),
            ...addedObstacles,
          ];
          const scheduledMovableObstacles = allObstacles.map((obstacle) => {
            return {
              ...obstacle,
              position: {
                ...obstacle.position,
                stack_id: scheduledStackId, // Update to match scheduled stack ID
              },
            };
          });
          const scheduledStack: StackType = {
            id: scheduledStackId,
            name: `Scheduled View - ${currentTimestamp.toLocaleString()}`,
            location_id: baseHangar.id,
            isReference: false,
            tenants: scheduledTenants,
            movableObstacles: scheduledMovableObstacles,
            options: baseHangar.stack.options,
          };
          // 7. Create complete scheduled hangar object
          const scheduledHangarData: Hangar = {
            ...baseHangar,
            stack: scheduledStack,
            stacks: [scheduledStack], // Replace stacks with just the scheduled one
          };
          // Wrap state updates in startTransition to prevent remounting
          startTransition(() => {
            setScheduledHangar(scheduledHangarData);
            setTenantTripMap(tripMap);
            setHasLoadedOnce(true);
          });
        } catch (error) {
          console.error("Failed to load scheduled data:", error);
          snackbar.notify({
            text: "Failed to load scheduled placement data",
            severity: "error",
          });
          setHasLoadedOnce(true);
        } finally {
          setInitialLoading(false);
          setIsUpdating(false);
        }
      };

      loadScheduledData();
    }, [
      postgrest,
      activeFBO,
      baseHangar,
      currentTimestamp,
      id,
      refreshKey,
      addedObstacles,
    ]);
    // Note: baseHangar back in dependencies so effect runs when base data loads

    // Handler for time changes - now managed by URL hook which handles snapping
    const handleTimeChange = React.useCallback(
      (newTime: Date) => {
        setCurrentTimestamp(newTime); // URL hook handles snapping internally
        // Update the viewState to track time changes for undo
        if (scheduledHangar) {
          setViewState({ scheduledHangar, timestamp: newTime });
        }
      },
      [setCurrentTimestamp, scheduledHangar, setViewState]
    );

    // Handler for jumping to next trip
    const handleJumpToNextTrip = React.useCallback(async () => {
      if (!placementService || !id) {
        return;
      }

      try {
        // Simple: if aircraft selected, search for that aircraft. Otherwise, any aircraft.
        const nextTripTime = selectedTripInfo?.tailNumber
          ? await placementService.getNextTripTimeForAircraft(
              selectedTripInfo.tailNumber,
              currentTimestamp,
              id,
              "hangar"
            )
          : await placementService.getNextTripTime(
              id,
              "hangar",
              currentTimestamp
            );

        if (nextTripTime) {
          // Navigate to the next trip
          const snappedTime = ceilToQuarterHour(nextTripTime);
          setCurrentTimestamp(snappedTime);

          // Update time range
          const { start: newStart, end: newEnd } =
            getDefaultWeekRange(snappedTime);
          setTimeRange(newStart, newEnd);
        }
      } catch (error) {
        console.error("Failed to jump to next trip:", error);
        snackbar.notify({
          text: "Failed to jump to next trip",
          severity: "error",
        });
      }
    }, [
      placementService,
      id,
      currentTimestamp,
      setCurrentTimestamp,
      getDefaultWeekRange,
      setTimeRange,
      selectedTripInfo,
    ]);

    // Handler for jumping to previous trip
    const handleJumpToPreviousTrip = React.useCallback(async () => {
      if (!placementService || !id) return;

      try {
        // Simple: if aircraft selected, search for that aircraft. Otherwise, any aircraft.
        const previousTripTime = selectedTripInfo?.tailNumber
          ? await placementService.getPreviousTripTimeForAircraft(
              selectedTripInfo.tailNumber,
              currentTimestamp,
              id,
              "hangar"
            )
          : await placementService.getPreviousTripTime(
              id,
              "hangar",
              currentTimestamp
            );

        if (previousTripTime) {
          // Navigate to the previous trip
          const snappedTime = floorToQuarterHour(previousTripTime);
          setCurrentTimestamp(snappedTime);

          // Update time range
          const { start: newStart, end: newEnd } =
            getDefaultWeekRange(snappedTime);
          setTimeRange(newStart, newEnd);
        }
      } catch (error) {
        console.error("Failed to jump to previous trip:", error);
        snackbar.notify({
          text: "Failed to jump to previous trip",
          severity: "error",
        });
      }
    }, [
      placementService,
      id,
      currentTimestamp,
      setCurrentTimestamp,
      getDefaultWeekRange,
      setTimeRange,
      selectedTripInfo,
    ]);

    // Handler for undo - restores both hangar state and timestamp
    const handleUndo = React.useCallback(() => {
      const previousState = undoViewState();
      if (previousState) {
        // Restore the timestamp first
        setCurrentTimestamp(previousState.timestamp);
        // The hangar state is already restored by undoViewState
        // Trigger a refresh to ensure UI is consistent
        setRefreshKey((prev) => prev + 1);
      }
    }, [undoViewState, setCurrentTimestamp, setRefreshKey]);

    // Handler for hangar changes (when user drags aircraft in scheduled view)
    const handleHangarChange = React.useCallback(
      async (updatedHangar: Hangar) => {
        // Update the scheduled hangar immediately for UI feedback
        setScheduledHangar(updatedHangar);

        if (!scheduledHangar || !postgrest) return;

        try {
          // Get current placements for comparison
          const oldTenants = scheduledHangar.stack.tenants;
          const newTenants = updatedHangar.stack.tenants;
          const oldObstacles = scheduledHangar.stack.movableObstacles;
          const newObstacles = updatedHangar.stack.movableObstacles;

          // Handle aircraft/tenant removals first
          for (const oldTenant of oldTenants) {
            const stillExists = newTenants.find((t) => t.id === oldTenant.id);

            if (!stillExists) {
              // Tenant was removed
              const tripId = tenantTripMap.get(oldTenant.id);

              if (tripId) {
                // Remove from location using the placement service
                const startTimeISO = currentTimestamp.toISOString();
                await placementService.assignToLocationWithGapFilling(
                  tripId,
                  null, // null location means unassigned
                  null,
                  startTimeISO,
                  undefined,
                  true, // excludeConflicts
                  { x: null, y: null, angle: null }
                );
              }
            }
          }

          // Handle aircraft/tenant position changes
          for (const newTenant of newTenants) {
            const oldTenant = oldTenants.find((t) => t.id === newTenant.id);

            if (!oldTenant) continue; // Skip new tenants (shouldn't happen in scheduled view)

            const oldPos = oldTenant.position;
            const newPos = newTenant.position;

            // Check if position changed (x, y, angle)
            const positionChanged =
              oldPos.x !== newPos.x ||
              oldPos.y !== newPos.y ||
              oldPos.angle !== newPos.angle;

            if (positionChanged) {
              // Get trip ID from our map
              const tripId = tenantTripMap.get(newTenant.id);

              if (!tripId) {
                console.error("No trip ID found for tenant:", newTenant.id);
                continue;
              }

              // Use database function to handle the placement change atomically
              // This ensures the aircraft stays in the new position until explicitly moved again
              await placementService.moveAircraftAtTime(
                tripId,
                id, // hangar id
                "hangar",
                currentTimestamp, // When the move happens
                {
                  x: newPos.x,
                  y: newPos.y,
                  angle: newPos.angle,
                }
              );
            }
          }

          // Handle movable obstacle position changes
          for (const newObstacle of newObstacles) {
            const oldObstacle = oldObstacles.find(
              (o) => o.id === newObstacle.id
            );

            if (!oldObstacle) continue; // Skip new obstacles (shouldn't happen in scheduled view)

            const oldPos = oldObstacle.position;
            const newPos = newObstacle.position;

            // Check if obstacle position changed (x, y, angle)
            const obstaclePositionChanged =
              oldPos.x !== newPos.x ||
              oldPos.y !== newPos.y ||
              oldPos.angle !== newPos.angle;

            if (obstaclePositionChanged) {
              // Check if this obstacle is from base or added obstacles
              const isFromBase = baseHangar?.stack.movableObstacles.find(
                (mo) => mo.id === newObstacle.id
              );
              const isFromAdded = addedObstacles.find(
                (mo) => mo.id === newObstacle.id
              );

              if (isFromBase && baseHangar) {
                // Update the base hangar's movable obstacle position
                const updatedBaseHangar = {
                  ...baseHangar,
                  stack: {
                    ...baseHangar.stack,
                    movableObstacles: baseHangar.stack.movableObstacles.map(
                      (mo) => (mo.id === newObstacle.id ? newObstacle : mo)
                    ),
                  },
                };
                setBaseHangar(updatedBaseHangar);
              } else if (isFromAdded) {
                // Update the added obstacles state
                setAddedObstaclesWithLogging(
                  addedObstacles.map((mo) =>
                    mo.id === newObstacle.id ? newObstacle : mo
                  )
                );
              }

              // Save obstacle position change to database immediately
              try {
                await postgrest.from("position").upsert({
                  id: newObstacle.position.id,
                  stack_id: baseHangar?.stack.id, // Use base stack ID for persistence
                  entity_id: newObstacle.entity_id,
                  x: newObstacle.position.x,
                  y: newObstacle.position.y,
                  angle: newObstacle.position.angle,
                  preferences: newObstacle.position.preferences || [],
                });
              } catch (error: any) {
                console.error("Failed to save obstacle position:", error);
                snackbar.notify({
                  text: "Failed to save obstacle position",
                  severity: "error",
                });
              }
            }
          }

          // Handle movable obstacle removals
          const currentObstacleCount =
            scheduledHangar.stack.movableObstacles.length;
          const newObstacleCount = updatedHangar.stack.movableObstacles.length;

          if (newObstacleCount < currentObstacleCount) {
            // Find removed obstacles
            const removedObstacles =
              scheduledHangar.stack.movableObstacles.filter(
                (oldObstacle) =>
                  !updatedHangar.stack.movableObstacles.find(
                    (newObs) => newObs.id === oldObstacle.id
                  )
              );

            for (const removed of removedObstacles) {
              // Check if it's from base or added obstacles
              const isFromBase = baseHangar?.stack.movableObstacles.find(
                (base) => base.id === removed.id
              );
              const isFromAdded = addedObstacles.find(
                (added) => added.id === removed.id
              );

              if (isFromBase && baseHangar) {
                // Delete from database and update base hangar
                await postgrest
                  .from("position")
                  .delete()
                  .eq("id", removed.position.id);
                await postgrest
                  .from("movable_obstacle")
                  .delete()
                  .eq("id", removed.id);

                const updatedBaseHangar = {
                  ...baseHangar,
                  stack: {
                    ...baseHangar.stack,
                    movableObstacles: baseHangar.stack.movableObstacles.filter(
                      (obs) => obs.id !== removed.id
                    ),
                  },
                };
                setBaseHangar(updatedBaseHangar);
              } else if (isFromAdded) {
                // Remove from added obstacles state
                setAddedObstacles((prev) =>
                  prev.filter((obs) => obs.id !== removed.id)
                );
              }
            }
          }
        } catch (error: any) {
          console.error("Failed to save hangar changes:", error);
          // Error handling via snackbar if needed
          snackbar.notify({
            text: error.message || "Failed to save changes",
            severity: "error",
          });
        }
      },
      [
        scheduledHangar,
        baseHangar,
        setBaseHangar,
        addedObstacles,
        postgrest,
        currentTimestamp,
        id,
        tenantTripMap,
        snackbar,
      ]
    );

    // Handler for aircraft removal - only updates local state
    // The database update happens through handleHangarChange
    const handleAircraftRemoval = React.useCallback(
      (tenantId: string) => {
        if (!scheduledHangar) return;

        // Update the local state (this will be captured for undo)
        const updatedHangar = {
          ...scheduledHangar,
          stack: {
            ...scheduledHangar.stack,
            tenants: scheduledHangar.stack.tenants.filter(
              (t) => t.id !== tenantId
            ),
          },
        };

        // Update through handleHangarChange which will:
        // 1. Update local state (for immediate UI feedback)
        // 2. Trigger database sync (handleHangarChange handles this)
        // 3. Enable proper undo (state is tracked before change)
        handleHangarChange(updatedHangar);
      },
      [scheduledHangar, handleHangarChange]
    );
    // Navigation options for the location dropdown
    const isSchedulingEnabled = activeFBO?.settings?.enableScheduling ?? false;

    // Use the actual time range from the store for metrics
    const timeRange = React.useMemo(
      () => ({
        start: rangeStart,
        end: rangeEnd,
      }),
      [rangeStart, rangeEnd]
    );

    return (
      <ScheduledHangarViewPresenter
        id={id}
        stacks={stacks}
        setStacks={setStacks}
        isReference={isReference}
        isAlgorithmTesting={isAlgorithmTesting}
        baseHangar={baseHangar}
        scheduledHangar={scheduledHangar}
        currentTimestamp={currentTimestamp}
        initialLoading={initialLoading}
        isUpdating={isUpdating}
        isPending={isPending}
        hasLoadedOnce={hasLoadedOnce}
        hasNextTrip={
          selectedTripInfo?.tailNumber ? hasNextTripForAircraft : hasNextTrip
        }
        hasPreviousTrip={
          selectedTripInfo?.tailNumber
            ? hasPreviousTripForAircraft
            : hasPreviousTrip
        }
        showAddObstacleDialog={showAddObstacleDialog}
        isSchedulingEnabled={isSchedulingEnabled}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        tenantTripMap={tenantTripMap}
        placementsInRange={allPlacementsInTimeRange}
        tripCountsByDate={tripCountsByDate}
        predefinedObstacles={predefinedObstacles}
        originalStackId={originalStackId}
        addedObstacles={addedObstacles}
        timeRange={timeRange}
        selectedTripInfo={selectedTripInfo}
        handleTimeChange={handleTimeChange}
        handleJumpToNextTrip={handleJumpToNextTrip}
        handleJumpToPreviousTrip={handleJumpToPreviousTrip}
        handleHangarChange={handleHangarChange}
        handleUndo={handleUndo}
        handleAircraftRemoval={handleAircraftRemoval}
        setShowAddObstacleDialog={setShowAddObstacleDialog}
        setRefreshKey={setRefreshKey}
        setAddedObstaclesWithLogging={setAddedObstaclesWithLogging}
        placementService={placementService}
      />
    );
  });
